/**
 * Cart-line “Add to Wishlist” — talks to Wysh (same guest crypto + /a/wysh API).
 * Collection/PDP hearts use Wysh placeholders; this covers the cart drawer only.
 */
const WYSH_CACHE_KEY = '_wysh-key';
const WYSH_SIG_HEADER = 'X-Wishlist-Signature';
const WYSH_TS_FIELD = 'signature_timestamp';
const WYSH_IDB = 'wysh-guest-crypto';
const WYSH_STORE = 'keys';
const WYSH_PRIV = 'guest-private-key';
const WYSH_PUB = 'guest-public-key';

function proxyPath(path = '') {
  let base = '/a/wysh';
  if (typeof window.wysh_proxy_base === 'string' && window.wysh_proxy_base) {
    base = window.wysh_proxy_base.replace(/\/+$/, '');
    if (!base.includes('://') && base.startsWith('//')) {
      base = base.replace(/^\/+/, '/');
    }
    base = base
      .replace(/\/([a-z]{2})a\/wysh/i, '/$1/a/wysh')
      .replace(/([^:])\/{2,}/g, '$1/');
  }
  if (!path || path === '/') return `${base}/`;
  return `${base}${path.startsWith('/') ? path : `/${path}`}`;
}

function idbRequest(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('IndexedDB request failed'));
  });
}

async function openGuestDb() {
  return new Promise((resolve, reject) => {
    const open = indexedDB.open(WYSH_IDB, 1);
    open.onupgradeneeded = () => {
      const db = open.result;
      if (!db.objectStoreNames.contains(WYSH_STORE)) db.createObjectStore(WYSH_STORE);
    };
    open.onsuccess = () => resolve(open.result);
    open.onerror = () => reject(open.error || new Error('Failed to open guest crypto database'));
  });
}

async function idbGet(key) {
  const db = await openGuestDb();
  try {
    return await idbRequest(db.transaction(WYSH_STORE, 'readonly').objectStore(WYSH_STORE).get(key));
  } finally {
    db.close();
  }
}

async function idbPut(key, value) {
  const db = await openGuestDb();
  try {
    await idbRequest(db.transaction(WYSH_STORE, 'readwrite').objectStore(WYSH_STORE).put(value, key));
  } finally {
    db.close();
  }
}

function bufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

async function exportPublicKeyPem(publicKey) {
  const spki = await window.crypto.subtle.exportKey('spki', publicKey);
  const b64 = bufferToBase64(spki);
  const lines = b64.match(/.{1,64}/g)?.join('\n') ?? b64;
  return `-----BEGIN PUBLIC KEY-----\n${lines}\n-----END PUBLIC KEY-----`;
}

async function getGuestKeys() {
  const privateKey = await idbGet(WYSH_PRIV);
  const publicKeyPem = await idbGet(WYSH_PUB);
  if (privateKey && typeof publicKeyPem === 'string' && publicKeyPem) {
    return { privateKey, publicKeyPem };
  }
  const pair = await window.crypto.subtle.generateKey(
    { name: 'ECDSA', namedCurve: 'P-256' },
    false,
    ['sign', 'verify']
  );
  const pem = await exportPublicKeyPem(pair.publicKey);
  await Promise.all([idbPut(WYSH_PRIV, pair.privateKey), idbPut(WYSH_PUB, pem)]);
  return { privateKey: pair.privateKey, publicKeyPem: pem };
}

async function signPayload(body, { includeGuestPublicKey = true } = {}) {
  const { privateKey, publicKeyPem } = await getGuestKeys();
  const payload = {
    ...(includeGuestPublicKey ? { ...body, guest_public_key: body?.guest_public_key ?? publicKeyPem } : body),
    [WYSH_TS_FIELD]: Math.floor(Date.now() / 1000),
  };
  const rawPayload = JSON.stringify(payload);
  const signature = bufferToBase64(
    await window.crypto.subtle.sign(
      { name: 'ECDSA', hash: 'SHA-256' },
      privateKey,
      new TextEncoder().encode(rawPayload)
    )
  );
  return { rawPayload, payload, signature, publicKeyPem };
}

function readWyshWishlists() {
  try {
    const raw = localStorage.getItem(WYSH_CACHE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    // Like Wysh: a cache written by the other session type (e.g. still “customer”
    // after signing out) holds lists this visitor can't use — ignore it.
    const sessionType = window.wysh_customer_id ? 'customer' : 'guest';
    if (parsed?.wishlists && (parsed.type || 'guest') !== sessionType) return [];
    if (Array.isArray(parsed?.wishlists)) return parsed.wishlists;
    if (Array.isArray(parsed)) return parsed;
  } catch {
    /* ignore */
  }
  return [];
}

function countWyshWishlistItems(wishlists = readWyshWishlists()) {
  return wishlists.reduce((sum, list) => {
    const items = list?.items && typeof list.items === 'object' ? list.items : {};
    return sum + Object.keys(items).length;
  }, 0);
}

/**
 * Keep the header Wysh badge in sync when we mutate the cache
 * (Wysh only updates it for its own heart buttons).
 */
function updateWyshHeaderBadgeCount(wishlists = readWyshWishlists()) {
  const count = countWyshWishlistItems(wishlists);
  document.querySelectorAll('.wysh-header-badge-wrapper .wysh-badge-count').forEach((badge) => {
    const overflow = count > 99;
    badge.setAttribute('data-wysh-count', String(count));
    badge.classList.toggle('wysh-badge-count--overflow', overflow);
    // Match Wysh: empty text at 0 (CSS shows "(0)"); number otherwise.
    badge.textContent = overflow || count === 0 ? '' : String(count);
  });
  window.dispatchEvent(
    new CustomEvent('wysh:wishlists-changed', { detail: { count } })
  );
}

function writeWyshWishlists(wishlists) {
  const now = Date.now();
  localStorage.setItem(
    WYSH_CACHE_KEY,
    JSON.stringify({
      wishlists:
        Array.isArray(wishlists) && wishlists.length
          ? wishlists
          : [{ wishlist_key: null, name: 'Wishlist', items: {}, total: 0 }],
      expires_at: now + 300000,
      written_at: now,
      type: window.wysh_customer_id ? 'customer' : 'guest',
    })
  );
  // Header badge + button refresh run via the localStorage.setItem hook below.
  refreshAllWyshButtons();
}

function refreshAllWyshButtons() {
  document.querySelectorAll('fluevog-wishlist[data-product-id]').forEach((el) => {
    if (typeof el.syncFromCache === 'function') el.syncFromCache();
  });
}

// Same-tab Wysh cache writes (from Wysh hearts elsewhere) should update cart buttons.
(() => {
  const nativeSetItem = localStorage.setItem.bind(localStorage);
  localStorage.setItem = (key, value) => {
    const previous = key === WYSH_CACHE_KEY ? localStorage.getItem(WYSH_CACHE_KEY) : null;
    nativeSetItem(key, value);
    // Also refresh account wishlist when Wysh cache updates in this tab
    if (key === WYSH_CACHE_KEY && value !== previous) {
      queueMicrotask(refreshAllWyshButtons);
      queueMicrotask(updateWyshHeaderBadgeCount);
      queueMicrotask(() => {
        document.querySelectorAll('fluevog-account-wishlist').forEach((el) => {
          if (typeof el.render === 'function') el.render();
        });
      });
    }
  };
})();

/**
 * Added to cart → drop the whole product from every wishlist holding it
 * (the cart line has its own “Add to Wishlist”). Hooks fetch so every
 * add path is covered: PDP/quick add, wishlist tabs, Wysh page.
 */
(() => {
  if (window.__fvWishlistCartHook) return;
  window.__fvWishlistCartHook = true;

  const isCartAddUrl = (url) => {
    try {
      return /\/cart\/add(\.js)?$/i.test(new URL(url, window.location.origin).pathname);
    } catch {
      return false;
    }
  };

  /**
   * Wysh's /a/wysh list page keeps its own state, and its remove button opens a
   * confirm modal — so drop the card ourselves and mirror Wysh's count/empty UI.
   */
  const repaintWyshPage = (productId, removedKeys) => {
    const viewedKey = window.viewed_wishlist_key;
    if (!viewedKey || window.wysh_shared_wishlist_data !== undefined) return;
    if (!removedKeys.includes(viewedKey)) return;

    const selector = `.wysh-products .wysh-product[data-product-id="${CSS.escape(productId)}"]`;
    document.querySelectorAll(selector).forEach((card) => (card.closest('li') || card).remove());

    const list = readWyshWishlists().find((entry) => entry?.wishlist_key === viewedKey);
    const count = Object.keys(list?.items || {}).length;
    const countEl = document.querySelector('.wysh-items-count');
    const template = window.wyshTranslations?.[count === 1 ? 'item_count' : 'items_count'];
    if (countEl && typeof template === 'string') {
      countEl.textContent = template.replace('[[count]]', String(count));
    }
    if (count === 0) {
      const empty = document.querySelector('.wysh-empty-wishlist');
      const items = document.querySelector('.wysh-items');
      if (empty instanceof HTMLElement) empty.style.display = 'block';
      if (items instanceof HTMLElement) items.style.display = 'none';
    }
  };

  const removeAddedProducts = async (data) => {
    if (!data || data.status || data.errors) return;
    const lines = Array.isArray(data.items) ? data.items : [data];
    const productIds = [...new Set(lines.map((line) => line?.product_id).filter(Boolean).map(String))];

    const listsHolding = (productId) =>
      readWyshWishlists().filter((list) => list?.wishlist_key && list.items?.[productId]);

    for (const productId of productIds) {
      const keys = listsHolding(productId).map((list) => list.wishlist_key);
      if (!keys.length) continue;
      for (const wishlistKey of keys) {
        try {
          await removeFromWysh(productId, '', wishlistKey);
        } catch (error) {
          console.error(error);
        }
      }
      // A sync already in flight (ours or Wysh's — they share _wyshSyncInFlight)
      // can predate the DELETE and write the item back; resync once if so.
      if (listsHolding(productId).length) await syncWishlist();
      // Wysh's own PDP button only repaints on this document event (with productId).
      document.dispatchEvent(new CustomEvent('wysh:wishlists-changed', { detail: { productId } }));
      repaintWyshPage(productId, keys);
    }
  };

  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    const response = await originalFetch(input, init);
    const url = input instanceof Request ? input.url : String(input);
    if (response.ok && isCartAddUrl(url)) {
      response
        .clone()
        .json()
        .then(removeAddedProducts)
        .catch(() => {});
    }
    return response;
  };
})();

function itemMatchesVariant(item, variantId) {
  if (!item) return false;
  if (!item.variant_ids || item.variant_ids.length === 0) return true;
  if (!variantId) return true;
  return item.variant_ids.some((id) => String(id) === String(variantId));
}

function isProductSaved(productId, variantId) {
  const id = String(productId);
  return readWyshWishlists().some((list) => {
    const item = list?.items?.[id];
    return itemMatchesVariant(item, variantId);
  });
}

function listsHoldingProduct(productId, variantId) {
  const id = String(productId);
  return readWyshWishlists()
    .filter((list) => list?.wishlist_key && itemMatchesVariant(list.items?.[id], variantId))
    .map((list) => list.wishlist_key);
}

function defaultWishlistKey() {
  return readWyshWishlists()[0]?.wishlist_key || null;
}

async function syncWishlist(wishlistKey = null) {
  if (window._wyshSyncInFlight) return window._wyshSyncInFlight;

  window._wyshSyncInFlight = (async () => {
    try {
      const url = new URL(proxyPath('/sync'), window.location.origin);
      const body = {};
      if (wishlistKey) body.wishlist_key = wishlistKey;
      if (window.wysh_customer_id) {
        body.logged_in_customer_id = Number(window.wysh_customer_id);
        body.default_wishlist_name = 'Wishlist';
      }
      const signed = await signPayload(body, { includeGuestPublicKey: false });
      if (signed.payload.wishlist_key) {
        url.searchParams.set('wishlist_key', signed.payload.wishlist_key);
      }
      if (signed.payload.logged_in_customer_id) {
        url.searchParams.set('logged_in_customer_id', signed.payload.logged_in_customer_id);
      }
      if (signed.payload.default_wishlist_name) {
        url.searchParams.set('default_wishlist_name', signed.payload.default_wishlist_name);
      }
      url.searchParams.set(WYSH_TS_FIELD, signed.payload[WYSH_TS_FIELD]);
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
          Accept: 'application/json',
          [WYSH_SIG_HEADER]: signed.signature,
        },
      });
      if (!response.ok) throw new Error(`Wishlist sync failed: ${response.status}`);
      const json = await response.json();
      const wishlists = Array.isArray(json) ? json : [];
      writeWyshWishlists(wishlists);
      return wishlists;
    } catch (error) {
      console.error(error);
      return readWyshWishlists();
    } finally {
      window._wyshSyncInFlight = null;
    }
  })();

  return window._wyshSyncInFlight;
}

async function addToWysh(productId, variantId, wishlistKey) {
  const body = {
    product_id: Number(productId),
    variant_id: variantId ? Number(variantId) : null,
    wishlist_key: wishlistKey,
  };
  const isGuest = !window.wysh_customer_id;
  const signed = await signPayload(body, { includeGuestPublicKey: isGuest });
  const response = await fetch(new URL(proxyPath('/items'), window.location.origin).toString(), {
    method: 'POST',
    headers: {
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
      'Content-Type': 'application/json',
      Accept: 'application/json',
      [WYSH_SIG_HEADER]: signed.signature,
    },
    body: signed.rawPayload,
  });
  const json = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(`Failed to add to wishlist: ${response.status}`);
  if (json?.wishlist_key) await syncWishlist(json.wishlist_key);
  else await syncWishlist(wishlistKey);
  return json;
}

async function removeFromWysh(productId, variantId, wishlistKey) {
  if (!wishlistKey) throw new Error('No wishlist key found');

  const lists = readWyshWishlists();
  const list = lists.find((entry) => entry.wishlist_key === wishlistKey);
  const item = list?.items?.[String(productId)];
  // Product-level wishlist (no sizes): omit variant_id so the whole item is removed.
  const hasSpecificVariants = Array.isArray(item?.variant_ids) && item.variant_ids.length > 0;
  const body = { product_id: Number(productId), wishlist_key: wishlistKey };
  if (hasSpecificVariants && variantId) body.variant_id = Number(variantId);

  const isGuest = !window.wysh_customer_id;
  const signed = await signPayload(body, { includeGuestPublicKey: isGuest });
  const response = await fetch(new URL(proxyPath('/items'), window.location.origin).toString(), {
    method: 'DELETE',
    headers: {
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
      'Content-Type': 'application/json',
      Accept: 'application/json',
      [WYSH_SIG_HEADER]: signed.signature,
    },
    body: signed.rawPayload,
  });
  if (!response.ok) throw new Error(`Failed to remove from wishlist: ${response.status}`);
  await syncWishlist(wishlistKey);
}

class FluevogWishlist extends HTMLElement {
  connectedCallback() {
    this.productId = this.dataset.productId || '';
    this.variantId = this.dataset.variantId || '';
    this.handle = this.dataset.handle || '';
    this.button = this.querySelector('[ref="button"]');
    this.label = this.querySelector('[ref="label"]');
    this.defaultLabel = this.dataset.defaultLabel || 'Add to Wishlist';
    this.savedLabel = this.dataset.savedLabel || 'In Wishlist';

    if (!this.button) return;
    // Cart uses Wysh product id; legacy handle mode kept as last resort.
    if (!this.productId && !this.handle) return;

    this._onStorage = (event) => {
      if (!event.key || event.key === WYSH_CACHE_KEY) this.syncFromCache();
    };
    window.addEventListener('storage', this._onStorage);

    this.syncFromCache();
    this.button.addEventListener('click', this.onClick.bind(this));
  }

  disconnectedCallback() {
    if (this._onStorage) window.removeEventListener('storage', this._onStorage);
  }

  syncFromCache() {
    if (this.productId) {
      this.render(isProductSaved(this.productId, this.variantId));
      return;
    }
    // Legacy local handles (should not be used once cart is on Wysh)
    try {
      const raw = localStorage.getItem('fv:wishlist');
      const handles = raw ? JSON.parse(raw) : [];
      this.render(Array.isArray(handles) && handles.includes(this.handle));
    } catch {
      this.render(false);
    }
  }

  async onClick(event) {
    event.preventDefault();
    event.stopPropagation();
    if (this.button.disabled) return;

    if (!this.productId) {
      this.toggleLocalHandle();
      return;
    }

    const saved = isProductSaved(this.productId, this.variantId);
    this.button.disabled = true;
    this.classList.add('is-loading');

    try {
      if (saved) {
        // The heart shows saved when *any* list holds the product, so clear it
        // from every list holding it — not just the default one.
        for (const wishlistKey of listsHoldingProduct(this.productId, this.variantId)) {
          await removeFromWysh(this.productId, this.variantId, wishlistKey);
        }
        // A sync already in flight can predate the DELETEs and write the item back.
        if (isProductSaved(this.productId, this.variantId)) await syncWishlist();
      } else {
        await addToWysh(this.productId, this.variantId, defaultWishlistKey());
      }
      this.syncFromCache();
    } catch (error) {
      console.error(error);
    } finally {
      this.button.disabled = false;
      this.classList.remove('is-loading');
    }
  }

  toggleLocalHandle() {
    try {
      const raw = localStorage.getItem('fv:wishlist');
      const handles = raw ? JSON.parse(raw) : [];
      const list = Array.isArray(handles) ? handles : [];
      const index = list.indexOf(this.handle);
      const saved = index === -1;
      if (saved) list.push(this.handle);
      else list.splice(index, 1);
      localStorage.setItem('fv:wishlist', JSON.stringify(list));
      this.render(saved);
    } catch {
      /* ignore */
    }
  }

  render(saved) {
    this.classList.toggle('is-saved', saved);
    this.button.setAttribute('aria-pressed', saved ? 'true' : 'false');
    if (this.label) {
      this.label.textContent = saved ? this.savedLabel : this.defaultLabel;
    }
  }
}

if (!customElements.get('fluevog-wishlist')) {
  customElements.define('fluevog-wishlist', FluevogWishlist);
}

export {
  WYSH_CACHE_KEY,
  addToWysh,
  defaultWishlistKey,
  proxyPath,
  readWyshWishlists,
  removeFromWysh,
  syncWishlist,
};
